import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';
import CampoCodigoBarras from './CampoCodigoBarras';

// Enum fixo (uFechamentoCartaoCredito.dfm:1594-1605 — comboTipo.Items.Strings)
const TIPOS_DOCUMENTO = [
  { value: '01', label: '01 - Boleto' },
  { value: '02', label: '02 - Contrato' },
  { value: '03', label: '03 - NF Outros (DANFE, Transporte e etc)' },
  { value: '04', label: '04 - Orçamento' },
  { value: '05', label: '05 - Pedido' },
  { value: '06', label: '06 - Recibo' },
  { value: '07', label: '07 - NFS/Produtor Rural' },
  { value: '08', label: '08 - Tributos' },
  { value: '09', label: '09 - RPA' },
  { value: '10', label: '10 - NFS MEI' },
  { value: '99', label: '99 - Outros' },
];

const TIPOS_DOCUMENTO_VALORES = TIPOS_DOCUMENTO.map((t) => t.value);

const apenasDigitos = (valor) => (valor ? String(valor).replace(/\D/g, '') : '');

// uFechamentoCartaoCredito.pas:1057-1097 — obrigatórios de verdade são: Cartão (já travado pela grid),
// Fornecedor, Departamento, Despesa/Conta, Vencimento e Forma de pagamento. Os demais campos aceitam
// string vazia no Delphi (eDoc.Text/eContabil.Text/edFavorecido.Text) e cCNPJ grava 0 se vazio.
const schema = Yup.object().shape({
  fornecedor: Yup.number().required('Fornecedor é obrigatório').positive().integer(),
  departamento: Yup.number().required('Departamento é obrigatório').positive().integer(),
  despesa: Yup.number().required('Despesa/Conta é obrigatória').positive().integer(),
  numero_documento: Yup.string().max(50).nullable(),
  codigo_contabil: Yup.string().max(10).nullable(),
  vencimento: Yup.date().required('Vencimento é obrigatório'),
  pagar_em: Yup.date()
    .nullable()
    .when('vencimento', (venc, sch) =>
      venc ? sch.max(venc, 'Pagar Em deve ser ≤ Vencimento') : sch
    ),
  tipocod: Yup.string().required('Forma de pagamento obrigatória').oneOf(['D', 'B']),
  tipo_documento: Yup.string()
    .required('Tipo de Documento obrigatório')
    .oneOf(TIPOS_DOCUMENTO_VALORES, 'Tipo de Documento inválido'),
  cnpj: Yup.string()
    .nullable()
    .test('numerico', 'Informe apenas dígitos', (v) => !v || apenasDigitos(v).length > 0),
  favorecido: Yup.string().max(100).nullable(),
  barras: Yup.string().when('tipocod', (tc, sch) =>
    tc === 'B'
      ? sch
          .required('Código de barras obrigatório')
          .test('barras-44-47', 'Código de barras deve ter 44 ou 47 dígitos', (v) => {
            const d = apenasDigitos(v).length;
            return d === 44 || d === 47;
          })
      : sch
  ),
  arquivo_pdf: Yup.mixed()
    .nullable()
    .test('file-type', 'Apenas arquivos .pdf', (v) => !v || (v && v.type === 'application/pdf'))
    .test('file-size', 'Arquivo máximo 10 MB', (v) => !v || (v && v.size <= 10 * 1024 * 1024)),
  // D-F-02 — rateio no Fechamento é sempre agregação automática (pas:1144-1150 + CarregaRateio).
  // Nenhum campo editável exposto ao usuário aqui.
});

const VALORES_INICIAIS = {
  fornecedor: null,
  fornecedor_raw: null,
  departamento: '',
  departamento_raw: null,
  despesa: '',
  numero_documento: '',
  codigo_contabil: '',
  vencimento: '',
  pagar_em: '',
  tipocod: 'D',
  tipo_documento: '01',
  cnpj: '',
  favorecido: '',
  obs1: '',
  obs2: '',
  obs3: '',
  obs4: '',
  barras: '',
  arquivo_pdf: null,
};

const formatarLabelFornecedor = (f) => {
  const nome = f.apelido_conta || f.razao_social || '';
  return `${f.codigo_conta} - ${nome}`.trim();
};

const formatarLabelDepartamento = (d) => {
  const desc = d.descricao || '';
  return `${d.codigo} - ${desc}`.trim();
};

const formatarLabelConta = (c) => {
  const desc = c.apelido_conta || '';
  return `${c.codigo_conta} - ${desc}`.trim();
};

// uFechamentoCartaoCredito.pas:480-482 — aplica default de Conta_Despesa_Cartao quando as opções
// estão carregadas e o usuário ainda não escolheu uma despesa.
const AplicarContaDespesaPadrao = ({ contaPadrao, opcoesDespesa }) => {
  const { values, setFieldValue } = useFormikContext();
  useEffect(() => {
    if (!contaPadrao) return;
    if (values.despesa) return;
    if (!opcoesDespesa.some((o) => o.value === contaPadrao)) return;
    setFieldValue('despesa', contaPadrao);
  }, [contaPadrao, opcoesDespesa, values.despesa, setFieldValue]);
  return null;
};

const PainelLancaChave = ({
  show,
  onClose,
  codigoGe,
  onConfirmar,
  loadingGravar,
  totalSelecionado = 0,
}) => {
  const [opcoesDepartamento, setOpcoesDepartamento] = useState([]);
  const [opcoesDespesa, setOpcoesDespesa] = useState([]);
  const [opcoesFornecedor, setOpcoesFornecedor] = useState([]);
  const [loadingFornecedores, setLoadingFornecedores] = useState(false);
  const [contaDespesaPadrao, setContaDespesaPadrao] = useState(null);

  useEffect(() => {
    api
      .obterContaDespesaPadraoCartao()
      .then((d) => setContaDespesaPadrao(d && d.codigo_conta ? Number(d.codigo_conta) : null))
      .catch(() => setContaDespesaPadrao(null));
  }, []);

  useEffect(() => {
    api
      .listarDepartamentosFechamentoCartao()
      .then((d) => {
        const lista = (d || []).map((dep) => ({
          value: dep.codigo,
          label: formatarLabelDepartamento(dep),
          raw: dep,
        }));
        setOpcoesDepartamento(lista);
      })
      .catch(() => setOpcoesDepartamento([]));
  }, []);

  useEffect(() => {
    if (!codigoGe) {
      setOpcoesDespesa([]);
      return;
    }
    api
      .listarContasFechamentoCartao(codigoGe)
      .then((c) => {
        const lista = (c || []).map((cnt) => ({
          value: cnt.codigo_conta,
          label: formatarLabelConta(cnt),
          raw: cnt,
        }));
        setOpcoesDespesa(lista);
      })
      .catch(() => setOpcoesDespesa([]));
  }, [codigoGe]);

  useEffect(() => {
    if (!codigoGe) {
      setOpcoesFornecedor([]);
      return;
    }
    setLoadingFornecedores(true);
    api
      .listarFornecedoresFechamentoCartao(codigoGe)
      .then((data) => {
        const lista = (data || []).map((f) => ({
          value: f.codigo_conta,
          label: formatarLabelFornecedor(f),
          raw: f,
        }));
        setOpcoesFornecedor(lista);
      })
      .catch(() => setOpcoesFornecedor([]))
      .finally(() => setLoadingFornecedores(false));
  }, [codigoGe]);

  return (
    <Modal show={show} onHide={onClose} size="xl" backdrop="static">
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ color: '#fff' }}>
          Gravar Fechamento — Dados do Título a Pagar
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={VALORES_INICIAIS}
        validationSchema={schema}
        onSubmit={onConfirmar}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
          const antecipaPgto = values.departamento_raw
            ? values.departamento_raw.antecipa_pgto === 'S'
            : false;

          const fornecedorValue = values.fornecedor_raw
            ? {
                value: values.fornecedor_raw.codigo_conta,
                label: formatarLabelFornecedor(values.fornecedor_raw),
                raw: values.fornecedor_raw,
              }
            : null;

          const departamentoValue = values.departamento
            ? opcoesDepartamento.find((o) => o.value === Number(values.departamento)) || null
            : null;

          const despesaValue = values.despesa
            ? opcoesDespesa.find((o) => o.value === Number(values.despesa)) || null
            : null;

          const tipoDocValue =
            TIPOS_DOCUMENTO.find((t) => t.value === values.tipo_documento) || null;

          const handleDeptoChange = (sel) => {
            if (!sel) {
              setFieldValue('departamento', '');
              setFieldValue('departamento_raw', null);
              setFieldValue('pagar_em', '');
              return;
            }
            setFieldValue('departamento', sel.value);
            setFieldValue('departamento_raw', sel.raw || null);
            if (!sel.raw || sel.raw.antecipa_pgto !== 'S') {
              setFieldValue('pagar_em', '');
            }
          };

          const handleFornecedorChange = (sel) => {
            if (!sel) {
              setFieldValue('fornecedor', null);
              setFieldValue('fornecedor_raw', null);
              setFieldValue('cnpj', '');
              setFieldValue('favorecido', '');
              setFieldValue('codigo_contabil', '');
              return;
            }
            setFieldValue('fornecedor', sel.value);
            setFieldValue('fornecedor_raw', sel.raw || null);
            if (sel.raw) {
              setFieldValue('cnpj', sel.raw.cpf_cnpj || '');
              setFieldValue('favorecido', sel.raw.razao_social || '');
              setFieldValue('codigo_contabil', sel.raw.codigo_contabil || '');
            }
          };

          return (
            <Form onSubmit={handleSubmit}>
              <AplicarContaDespesaPadrao
                contaPadrao={contaDespesaPadrao}
                opcoesDespesa={opcoesDespesa}
              />
              <Modal.Body>
                {/* Linha 1 — Fornecedor */}
                <Row>
                  <Col xs={12} className="mb-2">
                    <Form.Label className="small mb-1">Fornecedor *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesFornecedor}
                      value={fornecedorValue}
                      onChange={handleFornecedorChange}
                      placeholder={
                        codigoGe
                          ? (loadingFornecedores ? 'Carregando fornecedores...' : 'Selecione um fornecedor...')
                          : 'Selecione o GE (via despesas elegíveis)'
                      }
                      noOptionsMessage={() => 'Nenhum fornecedor'}
                      isClearable
                      isLoading={loadingFornecedores}
                      isDisabled={!codigoGe}
                    />
                    {touched.fornecedor && errors.fornecedor && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>
                        {errors.fornecedor}
                      </div>
                    )}
                  </Col>
                </Row>

                {/* Linha 2 — Nº Documento | Cód. Contábil */}
                <Row>
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Nº Documento *</Form.Label>
                    <Form.Control
                      type="text"
                      name="numero_documento"
                      maxLength={50}
                      value={values.numero_documento}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.numero_documento && !!errors.numero_documento}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.numero_documento}
                    </Form.Control.Feedback>
                  </Col>
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Cód. Contábil</Form.Label>
                    <Form.Control
                      type="text"
                      name="codigo_contabil"
                      maxLength={10}
                      value={values.codigo_contabil}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.codigo_contabil && !!errors.codigo_contabil}
                      placeholder="Auto-preenchido pelo fornecedor"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.codigo_contabil}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                {/* Linha 3 — CNPJ | Favorecido */}
                <Row>
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">CNPJ *</Form.Label>
                    <Form.Control
                      type="text"
                      name="cnpj"
                      maxLength={18}
                      value={values.cnpj}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.cnpj && !!errors.cnpj}
                      placeholder="Somente dígitos ou formatado"
                    />
                    <Form.Control.Feedback type="invalid">{errors.cnpj}</Form.Control.Feedback>
                  </Col>
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Favorecido *</Form.Label>
                    <Form.Control
                      type="text"
                      name="favorecido"
                      maxLength={100}
                      value={values.favorecido}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.favorecido && !!errors.favorecido}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.favorecido}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                {/* Linha 4 — Departamento | Despesa/Conta */}
                <Row>
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Departamento *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesDepartamento}
                      value={departamentoValue}
                      onChange={handleDeptoChange}
                      placeholder="Selecione..."
                      noOptionsMessage={() => 'Nenhum'}
                      isClearable
                    />
                    {touched.departamento && errors.departamento && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>
                        {errors.departamento}
                      </div>
                    )}
                  </Col>
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Despesa / Conta *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesDespesa}
                      value={despesaValue}
                      onChange={(sel) => setFieldValue('despesa', sel ? sel.value : '')}
                      placeholder="Selecione..."
                      noOptionsMessage={() => 'Nenhuma conta'}
                      isClearable
                      isDisabled={!codigoGe}
                    />
                    {touched.despesa && errors.despesa && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>
                        {errors.despesa}
                      </div>
                    )}
                  </Col>
                </Row>

                {/* Linha 5 — Tipo Documento | Vencimento | Pagar Em */}
                <Row>
                  <Col xs={12} md={4} className="mb-2">
                    <Form.Label className="small mb-1">Tipo de Documento *</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={TIPOS_DOCUMENTO}
                      value={tipoDocValue}
                      onChange={(sel) => setFieldValue('tipo_documento', sel ? sel.value : '')}
                      placeholder="Selecione..."
                      isSearchable={false}
                    />
                    {touched.tipo_documento && errors.tipo_documento && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>
                        {errors.tipo_documento}
                      </div>
                    )}
                  </Col>
                  <Col xs={6} md={4} className="mb-2">
                    <Form.Label className="small mb-1">Vencimento *</Form.Label>
                    <Form.Control
                      type="date"
                      name="vencimento"
                      value={values.vencimento}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.vencimento && !!errors.vencimento}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.vencimento}
                    </Form.Control.Feedback>
                  </Col>
                  <Col xs={6} md={4} className="mb-2">
                    <Form.Label className="small mb-1">
                      Pagar em (antecipação)
                      {!antecipaPgto && (
                        <span className="text-muted ml-1" style={{ fontSize: '0.7rem' }}>
                          (depto não antecipa)
                        </span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="pagar_em"
                      value={values.pagar_em || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!antecipaPgto}
                      isInvalid={touched.pagar_em && !!errors.pagar_em}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.pagar_em}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                {/* Linha 6 — Forma de pagamento (tipocod D/B) */}
                <Row>
                  <Col xs={12} className="mb-2">
                    <Form.Label className="small mb-1">Forma de pagamento *</Form.Label>
                    <div>
                      <Form.Check
                        type="radio"
                        id="tipocod-d"
                        name="tipocod"
                        label="Dinheiro / TED / PIX (linha digitável)"
                        value="D"
                        checked={values.tipocod === 'D'}
                        onChange={handleChange}
                        inline
                      />
                      <Form.Check
                        type="radio"
                        id="tipocod-b"
                        name="tipocod"
                        label="Boleto (código de barras)"
                        value="B"
                        checked={values.tipocod === 'B'}
                        onChange={handleChange}
                        inline
                      />
                    </div>
                    {touched.tipocod && errors.tipocod && (
                      <div className="text-danger" style={{ fontSize: '0.78rem' }}>
                        {errors.tipocod}
                      </div>
                    )}
                  </Col>
                </Row>

                {/* Linha 7 — Código de Barras (quando tipocod='B') */}
                {values.tipocod === 'B' && (
                  <CampoCodigoBarras
                    barras={values.barras}
                    tipocod={values.tipocod}
                    onChange={(campo, val) => setFieldValue(campo, val)}
                    errors={errors}
                    touched={touched}
                  />
                )}

                {/* Linha 8 — Observações */}
                <Row>
                  <Col xs={12} md={3} className="mb-2">
                    <Form.Label className="small mb-1">Obs 1</Form.Label>
                    <Form.Control
                      type="text"
                      name="obs1"
                      maxLength={50}
                      value={values.obs1}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col xs={12} md={3} className="mb-2">
                    <Form.Label className="small mb-1">Obs 2</Form.Label>
                    <Form.Control
                      type="text"
                      name="obs2"
                      maxLength={50}
                      value={values.obs2}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col xs={12} md={3} className="mb-2">
                    <Form.Label className="small mb-1">Obs 3</Form.Label>
                    <Form.Control
                      type="text"
                      name="obs3"
                      maxLength={50}
                      value={values.obs3}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col xs={12} md={3} className="mb-2">
                    <Form.Label className="small mb-1">Obs 4</Form.Label>
                    <Form.Control
                      type="text"
                      name="obs4"
                      maxLength={50}
                      value={values.obs4}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                {/* Linha 9 — Anexo PDF */}
                <Row className="mt-2">
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label className="small mb-1">Anexar PDF da Fatura *</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setFieldValue('arquivo_pdf', e.target.files[0] || null)}
                      isInvalid={touched.arquivo_pdf && !!errors.arquivo_pdf}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.arquivo_pdf}
                    </Form.Control.Feedback>
                    <small className="text-muted">Apenas .pdf, máx 10 MB</small>
                  </Col>
                </Row>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={loadingGravar}>
                  Cancelar
                </Button>
                <Button type="submit" variant="success" disabled={loadingGravar}>
                  {loadingGravar ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    'Confirmar Fechamento'
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default PainelLancaChave;
